// src/contexts/MemoryContext.tsx
// Central memory provider — wraps the app and makes onboarding memory,
// PDID, and journey state available to every tool without re-fetching.
// Reads from onboarding_memory.steps as the single source of truth.
// PDID and journey_states are referenced by ID only — no data duplication.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { OnboardingData } from '@/onboarding/types/onboarding.types';

// ── Types ─────────────────────────────────────────────────────────────
interface MemoryState {
  // Raw onboarding answers — the one source of truth
  steps: Partial<OnboardingData>;
  completed: boolean;
  onboardingMemoryId: string | null;

  // PDID status only — not a data duplicate
  pdidStatus: 'none' | 'pending' | 'active' | 'verified';
  pdidId: string | null;

  // Journey stage only — milestones/actions read separately when needed
  currentStage: string;
  journeyStateId: string | null;
}

interface MemoryContextType {
  // State
  memory: MemoryState;
  loading: boolean;

  // Most commonly needed fields — convenience shortcuts
  nationality: string;
  destination: string;
  purpose: string;
  education: string;
  profession: string;
  fullName: string;

  // Tool context — what any tool needs to pre-fill itself
  getContextForTool: (toolId: string) => ToolContext;

  // Refresh after onboarding completes
  refreshMemory: () => Promise<void>;
}

interface ToolContext {
  toolId: string;
  identity: {
    full_name: string;
    nationality: string;
    current_country: string;
    date_of_birth: string;
  };
  journey: {
    target_countries: string[];
    target_visa_types: string[];
    target_timeline: string;
    has_job_offer: boolean;
  };
  background: {
    education_level: string;
    occupation_title: string;
    work_experience_years: number;
  };
  financial: {
    savings: number;
    annual_income: number;
    currency: string;
  };
  immigration: {
    previous_visa_refusals: boolean;
    previous_applications: boolean;
    has_criminal_record: boolean;
  };
  preferences: {
    learning_style: string[];
  };
  consent: {
    data_consent: boolean;
    ai_assistant_consent: boolean;
  };
  hasCompletedOnboarding: boolean;
}

// ── Default empty state ───────────────────────────────────────────────
const defaultMemory: MemoryState = {
  steps: {},
  completed: false,
  onboardingMemoryId: null,
  pdidStatus: 'none',
  pdidId: null,
  currentStage: 'assessment',
  journeyStateId: null,
};

const MemoryContext = createContext<MemoryContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────
export const MemoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [memory, setMemory] = useState<MemoryState>(defaultMemory);
  const [loading, setLoading] = useState(true);

  const loadMemory = useCallback(async () => {
    if (!user?.id) {
      setMemory(defaultMemory);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Single query — onboarding_memory is the source of truth
      const { data: memData } = await supabase
        .from('onboarding_memory')
        .select('id, steps, completed')
        .eq('user_id', user.id)
        .maybeSingle();

      // PDID status only — not duplicating data
      const { data: pdidData } = await supabase
        .from('pdid')
        .select('id, status')
        .eq('user_id', user.id)
        .maybeSingle();

      // Journey stage only
      const { data: journeyData } = await supabase
        .from('journey_states')
        .select('id, current_stage')
        .eq('user_id', user.id)
        .maybeSingle();

      setMemory({
        steps: memData?.steps || {},
        completed: memData?.completed || false,
        onboardingMemoryId: memData?.id || null,
        pdidStatus: (pdidData?.status as MemoryState['pdidStatus']) || 'none',
        pdidId: pdidData?.id || null,
        currentStage: journeyData?.current_stage || 'assessment',
        journeyStateId: journeyData?.id || null,
      });
    } catch (error) {
      console.error('MemoryContext load error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load once auth is ready
  useEffect(() => {
    if (!authLoading) loadMemory();
  }, [authLoading, loadMemory]);

  // ── Convenience shortcuts ─────────────────────────────────────────
  const s = memory.steps;
  const nationality = s.nationality || '';
  const destination =
    s.destination ||
    s.journeyGoal?.destination ||
    (s.target_countries || [])[0] ||
    '';
  const purpose =
    s.purpose ||
    s.journeyGoal?.purpose ||
    (s.target_visa_types || [])[0] ||
    '';
  const education = s.education_level || '';
  const profession = s.occupation_title || '';
  const fullName = s.welcome_name || s.greeting || s.full_name || '';

  // ── Tool context builder ──────────────────────────────────────────
  const getContextForTool = useCallback((toolId: string): ToolContext => ({
    toolId,
    identity: {
      full_name: s.welcome_name || s.greeting || s.full_name || '',
      nationality: s.nationality || '',
      current_country: s.current_country || '',
      date_of_birth: s.date_of_birth || '',
    },
    journey: {
      target_countries: s.target_countries?.length
        ? s.target_countries
        : (s.destination || s.journeyGoal?.destination)
          ? [s.destination || s.journeyGoal?.destination]
          : [],
      target_visa_types: s.target_visa_types || [],
      target_timeline: s.target_timeline || '',
      has_job_offer: s.has_job_offer || false,
    },
    background: {
      education_level: s.education_level || '',
      occupation_title: s.occupation_title || '',
      work_experience_years: s.work_experience_years || 0,
    },
    financial: {
      savings: s.savings || 0,
      annual_income: s.annual_income || 0,
      currency: s.currency || 'GBP',
    },
    immigration: {
      previous_visa_refusals: s.previous_visa_refusals || false,
      previous_applications: s.previous_applications || false,
      has_criminal_record: s.has_criminal_record || false,
    },
    preferences: {
      learning_style: s.learning_style || [],
    },
    consent: {
      data_consent: s.data_consent || false,
      ai_assistant_consent: s.ai_assistant_consent || false,
    },
    hasCompletedOnboarding: memory.completed,
  }), [memory, s]);

  return (
    <MemoryContext.Provider value={{
      memory,
      loading,
      nationality,
      destination,
      purpose,
      education,
      profession,
      fullName,
      getContextForTool,
      refreshMemory: loadMemory,
    }}>
      {children}
    </MemoryContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────
export const useMemory = (): MemoryContextType => {
  const context = useContext(MemoryContext);
  if (!context) throw new Error('useMemory must be used within a MemoryProvider');
  return context;
};

// ── Convenience hook for tools ────────────────────────────────────────
// Any tool calls: const { context, hasMemory } = useToolMemory('uk-skilled-worker')
export const useToolMemory = (toolId: string) => {
  const { getContextForTool, loading, memory } = useMemory();
  return {
    context: getContextForTool(toolId),
    loading,
    hasMemory: memory.completed,
    nationality: memory.steps.nationality || '',
    destination:
      memory.steps.destination ||
      memory.steps.journeyGoal?.destination ||
      (memory.steps.target_countries || [])[0] ||
      '',
    education: memory.steps.education_level || '',
    profession: memory.steps.occupation_title || '',
  };
};
